import React, {useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useBalance } from '../../Data/Balance';
import { enableNewPlans, usePlans, descriptions, hidden, unreleased } from '../../Data/Plans';
import { PageLoading } from '../../Components/Loading';
import { classNames, useNewWindow } from '../../Services/helpers';
import Icon from '../../Components/Icons/Icon';
import Money from '../../Components/Formatting/Money';
import Button from '../../Components/Button/Button';
import AddCard from './AddCard';
import DateTime from '../../Components/Formatting/DateTime';
import { useDialogues } from '../../Components/Dialogues';
import Carousel, { Item } from '../../Components/Containers/Carousel/Carousel';
import Alert from '../../Components/Alert';

const Plan = () => {
    const {plans} = usePlans();
    const {balance, paymentMethod, changePlan, refresh} = useBalance(true);
    const [billing, setBilling] = useState('Yearly');
    const [addingCard, setAddingCard] = useState(false);
    const [requestedPlan, setRequestedPlan] = useState(null);
    const {confirm} = useDialogues();

    const selectPlan = (plan) => {
        setRequestedPlan(plan);
        if (!paymentMethod) {
            setAddingCard(true);
            return;
        }

        const currentPlan = plans.find(p => p.name === balance.plan);
        const anniversary = <DateTime date={balance.anniversary} hideTime />;
        const period = billing === 'Yearly' ? 'annually' : 'monthly';
        const amount = <Money pence={billing === 'Yearly' ? plan.yearly : plan.monthly} />;

        let message = null;

        // Changing the billing period of the current plan
        if (plan.id === currentPlan.id) {
            message = <>You're already on this plan. We'll start charging you {amount} {period} on {anniversary}.</>;

        // Changing plan while they're active
        } else if (plan.yearly > 0 && currentPlan.yearly > 0) {
            message = <>Your plan will be changed right away. Leftover credit will apply to the new plan, and any extra will be refunded.</>

            // Switching to a free plan
        } else if (currentPlan.yearly > 0 && plan.yearly === 0) {
            message = <>
                Your subscription will continue until {anniversary} then automatically downgraded to the free plan. You won't be charged again.
            </>;
        }

        confirm(
            {
                theme: 'primary',
                title: 'Confirm plan change',
                message: <>
                    Are you sure you want to change your plan to {plan.name}, billed {period} for {amount}?
                    <br /><br />
                    {message}
                </>
            },
            () => changePlan(plan.name, billing)
        )
    }

    const onCardAdded = async () => {
        const p = requestedPlan;
        setAddingCard(false);
        await refresh();
        setTimeout(() => {
            selectPlan(p);
        }, 200);
    }

    return <>
        <TopBar left="/billing" title="Plan" />
        <Page>
            {plans && balance ? <Inner {...{plans, balance, billing, setBilling, selectPlan}} /> : <PageLoading />}
        </Page>

        <AddCard isOpen={addingCard}
            setIsOpen={() => {
                setAddingCard(false);
                // Make sure they can request the plan again
                setTimeout(() => setRequestedPlan(null), 500);
            }}
            onComplete={onCardAdded}
        />
    </>
}

const Inner = ({plans, balance, billing, setBilling, selectPlan}) => {
    const currentPlan = plans.find(p => p.name === balance.plan);
    const [midPlan, topPlan] = enableNewPlans ? ['STANDARD', 'PRO'] : ['BASIC', 'PLUS']

    return <div className="mb-4">
        <LegacyPlan currentPlan={currentPlan} />
        <SubscriptionPeriod {...{billing, setBilling}} />
        <Carousel className="my-5">
            {enableNewPlans && currentPlan.legacy && <Item>
                <ComparePlan {...{balance, billing, selectPlan}} plan={currentPlan} />
            </Item>}
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === topPlan)} />
            </Item>
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === midPlan)} />
            </Item>
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'FREE')} />
            </Item>
        </Carousel>
        {/*
        <div className="flex flex-col md:flex-row flex-wrap space-4">
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'PRO')} />
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'STANDARD')} />
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'FREE')} />
        </div>
        */}
    </div>
}

const LegacyPlan = ({currentPlan}) => {
    const newWindow = useNewWindow();

    if (!enableNewPlans) {
        return <Alert
            theme="info"
            title="New plans coming soon"
            icon="CircleExclamation"
            message={<>If you choose a paid plan today you can stay on it with the same price for at least 6 months.<br />
                Find our more about it on our blog.
            </>}
            className="mb-4"
            buttons={[
                <Button theme="info" label="Read more" onClick={() => newWindow('https://getsoapy.com/blog/new-plans-coming-in-march')} />,
            ]}
        />
    }

    if (currentPlan.legacy !== true) {
        return null;
    }

    return <Alert
        theme="info"
        title="You're on a legacy plan"
        icon="CircleExclamation"
        message={<>That's ok for now, but you won't get any new features.<br />
            Please select a new plan to get the latest updates.
        </>}
        className="mb-4"
    />
}

const ComparePlan = ({plan, balance, billing, selectPlan}) => {
    return <div className=""> {/* md:w-[45%] w-full mx-auto my-4 p-4 border rounded-xl flex flex-col space-y-4 */}
        <div className="flex flex-row justify-between items-start">
            <h3 className="font-bold text-brand text-4xl">
                {plan.name}
                {plan.name === balance.plan && <Icon icon="CircleCheck" className="ml-2 fill-brand" />}
            </h3>
            <Price plan={plan} billing={billing} />
        </div>
        <p className="my-2 text-sm text-muted text-balance">
            {plan.legacy ? <>This is your current plan.<br />Please select one of our new plans.</> : descriptions[plan.id]}
        </p>

        {(!enableNewPlans || !plan.legacy) && <Button className="w-full"
            theme="primary"
            label={
                balance.plan === plan.name ?
                (billing === balance.subscriptionPeriod ? 'Current plan' : `Go ${billing}`) :
                'Select'
            }
            disabled={balance.plan === plan.name && billing === balance.subscriptionPeriod}
            onClick={() => selectPlan(plan)}
        />}
        {enableNewPlans && plan.legacy && <Button className="w-full"
            theme="primary"
            label="Legacy plan"
            disabled={true}
        />}

        <ul className="flex flex-col space-y-2">
            {Object.keys(plan.features).map(key => {
                const isUnreleased = unreleased.includes(key);

                if (hidden.includes(key) || isUnreleased) {
                    return null;
                }
                if (plan.id !== 'PLUS' && isUnreleased) {
                    return null;
                }

                const {name, enabled} = plan.features[key];
                return <li key={key} className={
                    classNames(!enabled || isUnreleased ? 'text-muted order-last' : 'text-gray-900 font-semibold')
                }>
                    <Icon icon={isUnreleased ? 'Clock' : (enabled ? 'Check' : 'XMark')}
                        className={classNames(
                            'mr-2',
                            !enabled || isUnreleased ? 'fill-muted' : 'fill-brand'
                        )}
                        theme={!enabled || isUnreleased ? 'light' : 'solid'}
                        inline
                    />
                    {name}
                    {isUnreleased && <span className="text-xs text-muted ml-2">Coming soon</span>}
                </li>
            })}
        </ul>
    </div>
}

const Price = ({plan, billing}) => {
    let big, small;
    if (plan.yearly === 0) {
        big = 'Free forever'
        small = <>&nbsp;</>;
    } else if (billing === 'Yearly') {
        big = <><Money pence={plan.yearly/12} />/m</>
        small = <>Billed <Money pence={plan.yearly} />/y</>
    } else {
        big = <><Money pence={plan.monthly} />/m</>
        small = <>Equiv. <Money pence={plan.monthly*12} />/y</>
    }

    return <div className="text-right text-nowrap">
        <span className="text-lg font-bold text-brand">{big}</span><br />
        <span className="text-sm text-muted">{small}</span><br />
        <span className="text-muted text-sm">
            {plan.monthly > 0 ? 'No VAT payable' : <>&nbsp;</>}
        </span>
    </div>
}

const SubscriptionPeriod = ({billing, setBilling}) => {
    const opt = (title, value) => {
        return <span className={classNames(
                'mx-1 py-2 px-3 inline-block cursor rounded-lg bg-slate-50 border',
                value === billing ? 'font-semibold text-brand border-brand' : 'text-muted border-slate-200',
            )}
            onClick={() => setBilling(value)}
        >
            {title}
        </span>
    }
    return <div className="my-2">
        <div className="flex flex-row-reverse leading-none">
            {opt('Monthly', 'Monthly')}
            {opt('Annually', 'Yearly')}
            <span className="my-2 inline-block text-muted mr-2">
                Billed:
            </span>
        </div>
    </div>
}

export default Plan;
